import { useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { ChevronLeftIcon } from '../../vendor/tailadmin/icons';

/**
 * Zpět tlačítko (#72026, Filip 8.6.2026).
 *
 * Browser history back — funguje stejně všude, předvídatelně. Skryje se:
 *   • na `/` (home rozcestník) — odtud zpět nedává smysl (history nás by
 *     dostala na `/login` z předchozí session)
 *   • když je history prázdná (po hard refresh, direct link s `?redirect=`)
 *
 * Použití: vykreslit v app-layout vedle drobečků. Standalone — nemá
 * žádnou závislost na trase, jen na router historii.
 */
export function BackButton() {
  const router = useRouter();
  const [canGoBack, setCanGoBack] = useState(false);

  useEffect(() => {
    // TanStack Router history má `canGoBack()` v některých verzích, ale
    // pro spolehlivost se opřeme o `window.history.length` (≥2 = aspoň
    // jeden záznam před aktuálním) + sledování `popstate` pro re-eval.
    const update = () => {
      // length 1 = jen aktuální stránka (čerstvý tab). Skrýt button.
      setCanGoBack(typeof window !== 'undefined' && window.history.length > 1);
    };
    update();
    window.addEventListener('popstate', update);
    return () => window.removeEventListener('popstate', update);
  }, []);

  const onClick = useCallback(() => {
    router.history.back();
  }, [router]);

  if (!canGoBack) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Zpět na předchozí stránku"
      className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-brand-500/40 dark:hover:bg-white/[0.05] dark:hover:text-brand-400"
    >
      <ChevronLeftIcon aria-hidden="true" className="h-4 w-4" />
      <span>Zpět</span>
    </button>
  );
}
